import React from 'react';
import { CountryRegionData } from 'react-country-region-selector';
import languages from 'languages';
import { sortBy } from 'lodash';

/**
 * 获取语言选项列表
 */
export const getLanguageOptions = () => {
  const langs = languages.getAllLanguageCode().reduce((acc, langCode) => {
    const landDesc = languages.getLanguageInfo(langCode);
    acc[landDesc.name] = langCode;
    return acc;
  }, {});

  const langOptions = Object.keys(langs).sort().map(name => ({
    value: langs[name],
    label: <span style={{ zIndex: '1000' }}>{name}</span>,
  }));

  return [
    {
      value: 'any',
      label: <span style={{ zIndex: '1000' }}>Any Language</span>
    },
    ...langOptions
  ];
};

/**
 * 获取国家选项列表
 */
export const getCountryOptions = () => [
  {
    value: 'Anywhere',
    label: <span style={{ zIndex: '1000' }}>Anywhere</span>,
  },
  ...CountryRegionData.map(c => ({
    value: c[0],
    label: <span style={{ zIndex: '1000' }}>{c[0]}</span>,
  }))
];

/**
 * 获取Campaign选项列表
 */
export const getCampaignOptions = (campaigns) => {
  return campaigns.map(({ id, name }) => ({
    value: id,
    label: <span style={{ zIndex: '1000' }}>{name}</span>,
  }));
};

/**
 * 获取平台选项列表
 */
export const getPlatformOptions = (platforms = []) => {
  const grouped = platforms.reduce((acc, pl) => {
    if (!acc[pl.baseService]) {
      acc[pl.baseService] = [];
    }
    acc[pl.baseService].push(pl);
    return acc;
  }, {});

  return Object.keys(grouped).map(group => ({
    label: group,
    options: grouped[group].map(({ id, platform }) => ({
      value: id,
      label: <span style={{ zIndex: '1000' }}>{platform}</span>,
    })),
  }));
};


/**
 * 获取任务步骤类型选项
 * @param {boolean} questIsAffiliate - 是否是联盟任务 
 * @param {boolean} requiredPreapproval - 是否需要预批准
 * @returns {Array} 步骤类型选项列表
 */
export const getStepsOptions = (questIsAffiliate, requiredPreapproval) => {
  let stepTypeOptions = [
    {
      label: 'Banner',
      value: 'display-download-banner',
      type: 'display-download',
      subtype: 'banner',
    },
    {
      label: 'Build Stream',
      value: 'display-text-build-stream',
      type: 'display-text',
      subtype: 'build-stream',
    },
    {
      label: 'Clip',
      value: 'submit-link-clip',
      type: 'submit-link',
      subtype: 'clip',
    },
    {
      label: 'Checkbox',
      value: 'display-text-checkbox',
      type: 'display-text',
      subtype: 'checkbox',
    },
    {
      label: 'Game Directory',
      value: 'display-text-game-name',
      type: 'display-text',
      subtype: 'game-name',
    },
    {
      label: 'Game Key',
      value: 'distribute-key',
      type: 'distribute-key',
      subtype: false,
    },
    {
      label: 'Hashtag (Quest)',
      value: 'display-hashtags-quest',
      type: 'display-hashtags',
      subtype: 'quest',
    },
    {
      label: 'Hashtag (Tracking)',
      value: 'display-hashtags-tracking',
      type: 'display-hashtags',
      subtype: 'tracking',
    },
    {
      label: 'In-game Achievement',
      value: 'display-text-achievement',
      type: 'display-text',
      subtype: 'achievement',
    },
    {
      label: 'Link (Affiliate)',
      value: 'distribute-link-affiliate',
      type: 'distribute-link',
      subtype: 'affiliate',
    },
    {
      label: 'Link (Campaign)',
      value: 'distribute-link-campaign',
      type: 'distribute-link',
      subtype: 'campaign',
    },
    {
      label: 'Overlay',
      value: 'display-download-overlay',
      type: 'display-download',
      subtype: 'overlay',
    },
    {
      label: 'Stream Tracking',
      value: 'display-stream-time',
      type: 'display-stream-time',
      subtype: false,
    },
    {
      label: 'Submit Stream Schedule',
      value: 'submit-schedule',
      type: 'submit-text',
      subtype: 'schedule',
    },
    {
      label: 'Text (General)',
      value: 'display-text',
      type: 'display-text',
      subtype: false,
    },
    {
      label: 'Tweet',
      value: 'submit-tweet',
      type: 'submit-tweet',
      subtype: false,
    },
    {
      label: 'Verification',
      value: 'submit-text',
      type: 'submit-text',
      subtype: false,
    },
    {
      label: 'VOD',
      value: 'submit-link-vod',
      type: 'submit-link',
      subtype: 'vod',
    },
    {
      label: 'Submit link',
      value: 'submit-link',
      type: 'submit-link',
      subtype: false,
    },
    {
      label: 'Submit Content Media',
      value: 'submit-content-media',
      type: 'submit-content-media',
      subtype: false,
    },
    {
      label: 'Submit Text (Optional)',
      value: 'submit-text-optional',
      type: 'submit-text-optional',
      subtype: false,
    },
    {
      label: 'Multi-video Submission',
      value: 'multi-video-submission',
      type: 'multi-video-submission',
      subtype: "tracking",
    },
  ];

  // 如果是联盟任务，添加社交媒体选项
  if (questIsAffiliate) {
    const affiliateStepTypeOptions = [
      { label: 'Instagram', value: 'instagram' },
      { label: 'Facebook', value: 'facebook' },
      { label: 'Twitter', value: 'twitter' },
      { label: 'Youtube', value: 'youtube' },
    ];
    stepTypeOptions = [
      ...stepTypeOptions,
      ...affiliateStepTypeOptions,
    ];
  }

  // 按标签字母顺序排序
  return sortBy(stepTypeOptions, ['label']);
};

// Quest Type Options
export const QUEST_TYPE_OPTIONS = [
  { label: 'Basic Quest', value: 'basic' },
  { label: 'Streaming Quest', value: 'tiered-one-time' },
  // { label: 'General', value: 'default' },
  // { label: 'Multi Day', value: 'tiered-multi-days' },
  { label: 'Streaming Quest(Split&Rank)', value: 'tiered-auto' },
  { label: 'Secondary Creation', value: 'secondary-creation' }
];

// Status Options
export const STATUS_OPTIONS = ['active', 'archived', 'disabled'];

// Member Tiers Type Options 
export const MEMBER_TIERS_TYPE_OPTIONS = [
  { label: 'Twitch Username', value: 'twitch-username' },
  { label: 'Noiz Username', value: 'noiz-username' }
];

// Whitelist Type Options
export const WHITELIST_TYPE_OPTIONS = [
  { label: 'Twitch Username', value: 'twitch-username' },
  { label: 'Noiz Username', value: 'noiz-username' }
];
